import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DatasetIcon from '@mui/icons-material/Dataset';
import InfoIcon from '@mui/icons-material/Info';
import CheckIcon from '@mui/icons-material/Check';
import ImportCSVModalShell from './ImportCSVModalShell';
import { parseCSV, rowsToObjects, downloadCSVTemplate, getRowValue, MAX_FILE_SIZE_MB, MAX_ROWS_PREVIEW } from './importCSVUtils';
import { validateUnitRow, rowToFormPayload, buildUnitApiPayload } from './unitCreation';

const STEPS = ['Select CSV', 'Preview', 'Done'];
const TEMPLATE_FILENAME = 'units_import_template.csv';

const FIELD_DEFINITIONS = [
  { key: 'unit_number', label: 'Unit number', required: true },
  { key: 'status', label: 'Status', required: false },
  { key: 'floor', label: 'Floor', required: false },
  { key: 'tower_wing', label: 'Tower / Wing', required: false },
  { key: 'facing', label: 'Facing', required: false },
  { key: 'base_price', label: 'Base price (₹)', required: true },
  { key: 'carpet_area', label: 'Carpet area', required: false },
  { key: 'builtup_area', label: 'Built-up area', required: false },
  { key: 'super_builtup_area', label: 'Super built-up area', required: false },
];

const SAMPLE_ROW = [
  'A-301',
  'available',
  '3',
  'Tower A',
  'north',
  '12500000',
  '1200',
  '1350',
  '1450',
];

/** Validate one CSV row (delegates to unitCreation.validateUnitRow). */
function validateRow(row, index) {
  return validateUnitRow(row, index, { getRowValue, requireArea: false });
}

const CSVImportUnits = ({ open, onClose, onImport, configurationName, areaUnit = 'sqft' }) => {
  const inputRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [error, setError] = useState('');
  const [importCount, setImportCount] = useState(0);

  const headers = FIELD_DEFINITIONS.map((f) => f.key);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Only CSV files are allowed.');
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File size must be under ${MAX_FILE_SIZE_MB}MB.`);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const { headers: h, rows } = parseCSV(ev.target?.result ?? '');
        if (!h.length) {
          setError('First row must contain column names.');
          setSelectedFile(null);
          return;
        }
        setCsvData({ headers: h, rows });
        setActiveStep(1);
      } catch (err) {
        setError(err.message || 'Failed to parse CSV');
        setSelectedFile(null);
      }
      if (inputRef.current) inputRef.current.value = '';
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  const handleDownloadTemplate = () => {
    downloadCSVTemplate(headers, [SAMPLE_ROW], TEMPLATE_FILENAME);
  };

  const handleImport = () => {
    const objects = rowsToObjects(csvData.headers, csvData.rows);
    const apiPayloads = [];
    const validationErrors = [];
    objects.forEach((row, i) => {
      const result = validateRow(row, i);
      if (result.valid) {
        const formPayload = rowToFormPayload(row, getRowValue);
        apiPayloads.push(buildUnitApiPayload(formPayload, { areaUnit }));
      } else {
        validationErrors.push(...result.errors);
      }
    });
    if (validationErrors.length > 0 && apiPayloads.length === 0) {
      setError(validationErrors.slice(0, 3).join('. '));
      return;
    }
    if (apiPayloads.length > 0) {
      onImport?.(apiPayloads);
      setImportCount(apiPayloads.length);
      setActiveStep(2);
    }
  };

  const resetForm = () => {
    setActiveStep(0);
    setSelectedFile(null);
    setCsvData({ headers: [], rows: [] });
    setError('');
    setImportCount(0);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  const displayRows = csvData.rows.slice(0, MAX_ROWS_PREVIEW);
  const totalCount = csvData.rows.length;

  const renderContent = () => {
    if (activeStep === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <input ref={inputRef} type="file" accept=".csv" onChange={handleFileChange} style={{ display: 'none' }} />
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1.5,
              p: 3,
              mb: 2,
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => inputRef.current?.click()}
          >
            <DatasetIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.7, mb: 0.5 }} />
            <Typography variant="body2" fontWeight={600}>Select CSV file</Typography>
            <Typography variant="caption" color="text.secondary">First row = headers</Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, textAlign: 'left', bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <InfoIcon sx={{ fontSize: 18, color: 'info.main' }} />
              <Typography variant="caption" fontWeight={600}>Columns</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Required: unit_number, base_price. Optional: status, floor, tower_wing, facing, carpet_area, builtup_area, super_builtup_area, tags.
            </Typography>
          </Paper>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} sx={{ textTransform: 'none' }}>
            Download template
          </Button>
        </Box>
      );
    }

    if (activeStep === 1) {
      return (
        <Box sx={{ py: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            {selectedFile?.name} · {(selectedFile?.size / 1024).toFixed(1)} KB · {totalCount} rows
          </Typography>
          {csvData.headers.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240, mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: 'grey.100', py: 0.5 }}>#</TableCell>
                    {csvData.headers.slice(0, 4).map((h, i) => (
                      <TableCell key={i} sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: 'grey.100', py: 0.5 }}>{h}</TableCell>
                    ))}
                    {csvData.headers.length > 4 && <TableCell sx={{ fontSize: '0.7rem', bgcolor: 'grey.100', py: 0.5 }}>…</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRows.slice(0, 8).map((row, ri) => (
                    <TableRow key={ri}>
                      <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>{ri + 1}</TableCell>
                      {csvData.headers.slice(0, 4).map((h, ci) => (
                        <TableCell key={ci} sx={{ fontSize: '0.7rem', py: 0.5 }}>{row[ci] ?? '—'}</TableCell>
                      ))}
                      {csvData.headers.length > 4 && <TableCell sx={{ fontSize: '0.7rem', py: 0.5 }}>…</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button size="small" onClick={() => setActiveStep(0)} sx={{ textTransform: 'none' }}>Back</Button>
            <Button size="small" variant="contained" onClick={handleImport} disabled={totalCount === 0} startIcon={<UploadFileIcon />} sx={{ textTransform: 'none', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}>
              Create units ({totalCount})
            </Button>
          </Box>
        </Box>
      );
    }

    if (activeStep === 2) {
      return (
        <Box sx={{ py: 2, textAlign: 'center' }}>
          <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
          <Typography variant="body2">Imported {importCount} unit(s).</Typography>
          <Button size="small" variant="contained" onClick={handleClose} sx={{ mt: 2, textTransform: 'none', bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}>Done</Button>
        </Box>
      );
    }

    return null;
  };

  return (
    <ImportCSVModalShell
      open={open}
      onClose={handleClose}
      title="Import CSV — Units"
      subtitle={configurationName ? `Add units to ${configurationName}` : 'One unit per row.'}
      steps={STEPS}
      activeStep={activeStep}
      error={error}
      onErrorDismiss={() => setError('')}
    >
      {renderContent()}
    </ImportCSVModalShell>
  );
};

export default CSVImportUnits;
export { FIELD_DEFINITIONS, validateRow };
